import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { ArrowRight } from 'lucide-react';

const SITE_URL = 'https://www.patekarecycle.com';

const stats = [
  { value: '8,000', unit: 'MT', label: 'Annual waste collection capacity' },
  { value: '88%', unit: '', label: 'Average material recovery yield' },
  { value: '5,000+', unit: 'T', label: 'Recovered material target 2025' },
  { value: '15+', unit: '', label: 'Commercial partners' },
];

const impactPoints = [
  {
    num: '01',
    title: 'Multi-material Recovery',
    description: 'Plastics, metals, paper, and other materials recovered and channeled to authorized recyclers, diverting waste from landfills.',
  },
  {
    num: '02',
    title: 'Compliance-Ready Systems',
    description: 'Every waste stream handled with full documentation, traceability, and regulatory compliance — EPR, CPCB, and state-level requirements.',
  },
  {
    num: '03',
    title: 'ESG Reporting Support',
    description: 'Clients receive detailed material recovery reports and operational insights to support their ESG disclosures and sustainability goals.',
  },
  {
    num: '04',
    title: 'Community Impact',
    description: 'Circular economy programs for communities create local employment, improve waste segregation, and reduce environmental burden.',
  },
];

const whyPoints = [
  { title: 'Multi-material Recovery', desc: 'Expertise across plastics, metals, paper, e-waste, and more.' },
  { title: 'Transparent & Traceable', desc: 'Full traceability from waste source to authorized end-use.' },
  { title: 'Compliance-Ready', desc: 'Systems designed to meet EPR, CPCB, and state regulations.' },
  { title: 'Circular Economy Focus', desc: 'Operations designed to maximize material circularity.' },
  { title: 'ESG Reporting Support', desc: 'Detailed reports to support your sustainability disclosures.' },
  { title: 'Pan-India Presence', desc: 'Operations across multiple states with local expertise.' },
];

const galleryImages = [
  '/images/gallery-01.webp',
  '/images/gallery-03.webp',
  '/images/gallery-06.webp',
  '/images/gallery-10.webp',
  '/images/gallery-15.webp',
  '/images/gallery-19.webp',
];

export function Impact() {
  return (
    <Layout>
      <Helmet>
        <title>Our Impact | Punarvaa - Environmental Impact & Sustainability Results</title>
        <meta name="title" content="Our Impact | Punarvaa - Environmental Impact & Sustainability Results" />
        <meta name="description" content="Punarvaa's environmental impact: 8,000 MT annual capacity, 88% material recovery yield, 5,000+ tonnes recovered, 15+ commercial partners across India." />
        <meta name="keywords" content="Punarvaa impact, waste recovery India, environmental impact, sustainability results, ESG reporting, circular economy India" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE_URL}/impact`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/impact`} />
        <meta property="og:title" content="Our Impact | Punarvaa - Environmental Results" />
        <meta property="og:description" content="8,000 MT annual capacity, 88% material recovery yield, 5,000+ tonnes recovered, 15+ commercial partners." />
        <meta property="og:site_name" content="Punarvaa by Patekar Recycle" />
        <meta property="og:image" content={`${SITE_URL}/images/og-image.png`} />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@patekarrecycle" />
        <meta name="twitter:title" content="Our Impact | Punarvaa - Environmental Results" />
        <meta name="twitter:description" content="8,000 MT annual capacity, 88% material recovery yield, 5,000+ tonnes recovered, 15+ commercial partners." />
        <meta name="twitter:image" content={`${SITE_URL}/images/og-image.png`} />
      </Helmet>

      {/* Hero — dark editorial */}
      <section className="pt-32 pb-20 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-semibold tracking-widest uppercase text-p-500 mb-6">
              Impact & Sustainability
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[0.95] mb-6">
              Circular systems create<br />
              <span className="text-p-400">measurable impact.</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-xl">
              Every ton recovered reduces landfill, saves resources, and lowers emissions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats — large typographic numbers, dark bg */}
      <section className="bg-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-gray-800">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className="py-12 px-8 first:pl-0"
              >
                <div className="text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-2 tabular-nums leading-none">
                  {stat.value}
                  {stat.unit && <span className="text-p-500 text-3xl lg:text-4xl ml-1">{stat.unit}</span>}
                </div>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 2025 Targets strip */}
      <section className="py-8 bg-gray-900 border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            <p className="text-xs font-semibold tracking-widest uppercase text-gray-600">2025 Targets</p>
            {[
              '5000+ tonnes recovered annually',
              'Zero untreated waste to landfill',
              '15+ commercial partnerships',
            ].map((t) => (
              <div key={t} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-p-500 shrink-0" />
                <span className="text-sm text-gray-400">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How we create impact — editorial numbered list on white */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-4">How We Create Impact</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900">
              Structured recovery.<br />Measurable outcomes.
            </h2>
          </motion.div>

          <div>
            {impactPoints.map((point, index) => (
              <motion.div
                key={point.num}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: index * 0.07 }}
                className="flex items-start gap-8 lg:gap-12 py-6 border-b border-gray-100 last:border-0"
              >
                <span className="text-3xl lg:text-4xl font-bold text-gray-200 tabular-nums w-14 shrink-0 pt-0.5">
                  {point.num}
                </span>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{point.title}</h3>
                  <p className="text-gray-500 leading-relaxed max-w-2xl">{point.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo gallery — full bleed dark */}
      <section className="py-16 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold tracking-widest uppercase text-gray-600 mb-8">Operations Gallery</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {galleryImages.map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="overflow-hidden rounded-lg aspect-video"
              >
                <picture>
                  <source srcSet={src} type="image/webp" />
                  <img
                    src={src}
                    alt={`Punarvaa operations ${i + 1}`}
                    width={600}
                    height={338}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </picture>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Punarvaa — dark editorial list */}
      <section className="py-20 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <p className="text-xs font-semibold tracking-widest uppercase text-gray-600 mb-4">Why Punarvaa</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">Why businesses work with us.</h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-800">
            {whyPoints.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-gray-950 p-6 hover:bg-gray-900 transition-colors"
              >
                <div className="w-1 h-6 bg-p-600 mb-4" />
                <p className="font-bold text-white mb-1">{item.title}</p>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-p-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
                Ready to measure your impact?
              </h2>
              <p className="text-p-100 text-lg max-w-xl">
                Partner with Punarvaa to build a structured waste recovery system with full ESG reporting.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <Link
                to="/contact?audit=true"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-p-800 font-semibold rounded-xl hover:bg-p-50 transition-colors"
              >
                Request Assessment
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact?quote=true"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/30 text-white font-semibold rounded-xl hover:bg-white/10 transition-colors"
              >
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
